import React, { useState, useEffect, useRef } from "react";

interface VisualReactionProps {
  question: string;
  sequence: string[];
  targetColor: string;
  minDelay: number;
  maxDelay: number;
  onComplete: (score: number, reactionTimes: number[]) => void;
}

interface ColorMap {
  [key: string]: string;
}

const VisualReaction: React.FC<VisualReactionProps> = ({
  question,
  sequence,
  targetColor,
  minDelay,
  maxDelay,
  onComplete,
}) => {
  const sequenceIndexRef = useRef(-1);
  const [currentColor, setCurrentColor] = useState("");
  const [isWaitingForTargetClick, setIsWaitingForTargetClick] = useState(false);
  const [targetAppearedTime, setTargetAppearedTime] = useState(0);
  const [score, setScore] = useState(0);
  const [reactionTimes, setReactionTimes] = useState<number[]>([]);
  const [isPressed, setIsPressed] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  useEffect(() => {
    console.log("VisualReaction useEffect");
    sequenceIndexRef.current = -1;
    setCurrentColor("");
    setIsWaitingForTargetClick(false);
    setTargetAppearedTime(0);
    setScore(0);
    setReactionTimes([]);
    setIsPressed(false);
    setIsComplete(false);
    setIsStarted(false);
  }, [question]);

  const COLORS: ColorMap = {
    yellow: "#FFBE37", // honeycombYellow
    blue: "#BCDEEF", // skyBlue
    red: "#EF4444", // redIncorrect
    green: "#10B981", // greenCorrect
  };

  const startTask = () => {
    setIsStarted(true);
    sequenceIndexRef.current = -1;
    setScore(0);
    setReactionTimes([]);
    setIsComplete(false);

    // Add a small delay before the first color appears
    setTimeout(() => {
      startNextTrial();
    }, 1000);
  };

  const startNextTrial = () => {
    const nextIndex = sequenceIndexRef.current + 1;
    if (nextIndex >= sequence.length) {
      // Task complete
      setIsComplete(true);
      onComplete(score, reactionTimes);
      return;
    }

    // Update both the ref and the state
    sequenceIndexRef.current = nextIndex;
    const nextColor = sequence[nextIndex];
    setCurrentColor(nextColor);

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }

    // Calculate a random delay between minDelay and maxDelay
    const delay = Math.random() * (maxDelay - minDelay) + minDelay;

    if (nextColor === targetColor) {
      setIsWaitingForTargetClick(true);
      setTargetAppearedTime(Date.now());
    } else {
      setIsWaitingForTargetClick(false);
      setTargetAppearedTime(0);
    }

    // Set timeout for auto-progression if user doesn't click
    timeoutRef.current = setTimeout(() => {
      if (nextColor === targetColor) {
        // User missed a target
        processResult(false);
      } else {
        // Non-target color, just move to next
        startNextTrial();
      }
    }, delay);
  };

  const handleCircleClick = () => {
    if (isComplete) return;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }

    if (isWaitingForTargetClick && currentColor === targetColor) {
      processResult(true);
    } else {
      processResult(false);
    }
  };

  const processResult = (isCorrect: boolean) => {
    let rt = 0;
    let newScore = score;
    const newReactionTimes = [...reactionTimes];

    if (isCorrect) {
      rt = Date.now() - targetAppearedTime;
      newReactionTimes.push(rt);
      newScore += 1;
      // Show pressed effect
      setIsPressed(true);
    } else {
      newScore -= 0.5; // Deduct 0.5 for incorrect reaction
      // Show pressed effect
      setIsPressed(true);
    }

    setScore(newScore);
    setReactionTimes(newReactionTimes);
    setIsWaitingForTargetClick(false);
    setTargetAppearedTime(0);

    // Clear any existing timeout to prevent race conditions
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }

    // Briefly show pressed effect then move to next trial
    setTimeout(() => {
      setIsPressed(false); // Reset pressed state
      startNextTrial();
    }, 1000);
  };

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl mb-6 font-semibold">{question}</h2>

      <div className="text-center flex flex-col items-center justify-center">
        {!isStarted ? (
          <button
            onClick={startTask}
            className="bg-[#0F1D21] text-white font-bold py-2 px-6 rounded-lg hover:bg-gray-700 transition-colors duration-300 shadow-md"
          >
            Start Task
          </button>
        ) : (
          <>
            <div
              className="reaction-target w-[100px] h-[100px] rounded-full flex items-center justify-center font-bold text-xl shadow-md cursor-pointer"
              style={{
                backgroundColor:
                  currentColor && COLORS[currentColor]
                    ? COLORS[currentColor]
                    : "#F0F0F0",
                transition: "all 0.2s ease-in-out",
                transform: isPressed ? "scale(0.95)" : "scale(1)",
              }}
              onClick={handleCircleClick}
            />

            {isComplete && (
              <div className="mt-6">
                <p className="text-lg font-semibold">Task Complete!</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VisualReaction;
